import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Customer Product Match DTO Schema
 */
export const CustomerProductMatchSchema = z.object({
  id: z.uuid(),
  customerId: z.uuid(),
  productId: z.uuid(),
  productVariantId: z.uuid(),

  // Customer identifier fields
  customerDescription: z.string().nullable(),
  customerMainIdentifier: z.string().nullable(),
  customerSku: z.string().nullable(),
  customerUpc: z.string().nullable(),
  customerPlu: z.string().nullable(),
  customerEan: z.string().nullable(),
  customerGtin: z.string().nullable(),
  customerAsin: z.string().nullable(),
  customerMpn: z.string().nullable(),
  customerIsbn: z.string().nullable(),
  customerNsn: z.string().nullable(),
  customerFreeform: z.array(z.string()).nullable(),

  customer: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  product: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  productVariant: z.object({
    id: z.uuid(),
    name: z.string(),
    sku: z.string().nullable(),
    active: z.boolean(),
  }),

  matchSequence: z.number(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

/**
 * List Customer Product Matches Query Schema
 */
export const ListCustomerProductMatchesQuerySchema = z.object({
  customerId: z.uuid().optional(),
  productId: z.uuid().optional(),
  search: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).prefault(25),
  offset: z.coerce.number().int().min(0).prefault(0),
});

/**
 * List Customer Product Matches Response Schema
 */
export const ListCustomerProductMatchesResponseSchema = z.object({
  customerProductMatches: z.array(CustomerProductMatchSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

/**
 * Shared optional fields for customer product updates
 */
const CustomerProductOptionalFieldsSchema = z.object({
  customerDescription: z.string().nullable().optional(),
  customerMainIdentifier: z.string().nullable().optional(),
  customerSku: z.string().nullable().optional(),
  customerUpc: z.string().nullable().optional(),
  customerPlu: z.string().nullable().optional(),
  customerEan: z.string().nullable().optional(),
  customerGtin: z.string().nullable().optional(),
  customerAsin: z.string().nullable().optional(),
  customerMpn: z.string().nullable().optional(),
  customerIsbn: z.string().nullable().optional(),
  customerNsn: z.string().nullable().optional(),
  matchSequence: z.number().int().min(0).optional(),
});

/**
 * Create Customer Product input schema
 */
export const CreateCustomerProductInputSchema =
  CustomerProductOptionalFieldsSchema.extend({
    customerId: z.uuid(),
    productVariantId: z.uuid(),
    customerFreeform: z.array(z.string()).min(1),
  });

/**
 * Create Customer Product response schema
 */
export const CreateCustomerProductResponseSchema = z.object({
  customerProductId: z.uuid(),
  didCreate: z.boolean(),
});

/**
 * Update Customer Product input schema
 */
export const UpdateCustomerProductInputSchema =
  CustomerProductOptionalFieldsSchema.extend({
    customerProductId: z.uuid(),
    productVariantId: z.uuid().optional(),
    customerFreeform: z.array(z.string()).nullable().optional(),
  });

/**
 * Update Customer Product response schema
 */
export const UpdateCustomerProductResponseSchema = z.object({
  customerProductId: z.uuid(),
});

/**
 * Delete Customer Product input schema
 */
export const DeleteCustomerProductInputSchema = z.object({
  customerProductId: z.uuid(),
});

/**
 * Delete Customer Product response schema
 */
export const DeleteCustomerProductResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Customer Products Contracts
 */
export const customerProductsContracts = {
  list: oc
    .input(ListCustomerProductMatchesQuerySchema)
    .output(ListCustomerProductMatchesResponseSchema)
    .route({
      method: "GET",
      path: "/customer-products",
    }),
  create: oc
    .input(CreateCustomerProductInputSchema)
    .output(CreateCustomerProductResponseSchema)
    .route({
      method: "POST",
      path: "/customer-products",
    }),
  update: oc
    .input(UpdateCustomerProductInputSchema)
    .output(UpdateCustomerProductResponseSchema)
    .route({
      method: "PUT",
      path: "/customer-products",
    }),
  delete: oc
    .input(DeleteCustomerProductInputSchema)
    .output(DeleteCustomerProductResponseSchema)
    .route({
      method: "DELETE",
      path: "/customer-products",
    }),
} as const;

// Export types
export type CustomerProductMatch = z.infer<typeof CustomerProductMatchSchema>;
export type ListCustomerProductMatchesQuery = z.infer<
  typeof ListCustomerProductMatchesQuerySchema
>;
export type ListCustomerProductMatchesResponse = z.infer<
  typeof ListCustomerProductMatchesResponseSchema
>;
export type CreateCustomerProductInput = z.infer<
  typeof CreateCustomerProductInputSchema
>;
export type CreateCustomerProductResponse = z.infer<
  typeof CreateCustomerProductResponseSchema
>;
export type UpdateCustomerProductInput = z.infer<
  typeof UpdateCustomerProductInputSchema
>;
export type UpdateCustomerProductResponse = z.infer<
  typeof UpdateCustomerProductResponseSchema
>;
export type DeleteCustomerProductInput = z.infer<
  typeof DeleteCustomerProductInputSchema
>;
export type DeleteCustomerProductResponse = z.infer<
  typeof DeleteCustomerProductResponseSchema
>;
